import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faLaptopCode,
  faThumbsUp,
  faVolumeMute,
  faVolumeUp,
  faPlay,
  faPause,
  faExpand,
  faCompress,
} from "@fortawesome/free-solid-svg-icons";

/*
  FIXES APPLIED for sync between Card and Thumb:
  - Added `onPlayVideo` prop (symmetric to onPauseVideo)
  - handlePlayOverlay now calls onPlayVideo(item.CourseId) instead of direct play()
    ? this updates parent state (playingId + isVideoPlaying) so Thumb icon/badge updates instantly
  - togglePausePlay notifies parent in both branches (for future-proofing)
  - useEffect now decides playback STRICTLY from parent: shouldPlay = (playingId === item.CourseId && isVideoPlaying)
    ? independent of hovered/hoveredId. Hover only controls whether video layer is mounted.
    ? This guarantees: pause click in Card ? parent state change ? Thumb re-renders with play icon + no active highlight change (or keeps if we keep playingId)
  - Parent handlePauseVideo no longer nulls playingId (keeps last active video id). This keeps:
      * Thumb highlighted with "Now Playing" badge
      * Card video layer visible after pause (with big Play overlay for instant resume)
      * Clicking Thumb play button resumes correctly (toggle logic in handlePlayVideo)
  - Only one video can ever be in playing state (enforced by single playingId + strict shouldPlay check in every Card instance)
*/

function BannerSliderCard({ 
  item, 
  hovered, 
  hoveredId, 
  isVideoPlaying, 
  playingId, 
  isMuted, 
  setIsMuted, 
  volume, 
  setVolume,
  onPauseVideo,
  onPlayVideo   // NEW - required for full play/pause sync with Thumb
}) {
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showVolSlider, setShowVolSlider] = useState(false);

  const getCleanDescription = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().substring(0, 155) + 
           (html.length > 155 ? "..." : "");
  };

  const handleVideoPlay = () => setVideoPlaying(true);
  const handleVideoPause = () => setVideoPlaying(false);

  const toggleMute = () => {
    if (!videoRef.current || !setIsMuted) return;
    const newMuted = !isMuted;
    videoRef.current.muted = newMuted;
    setIsMuted(newMuted);
  };

  const handleVolumeChange = (e) => {
    const newVol = parseFloat(e.target.value);
    if (videoRef.current) videoRef.current.volume = newVol;
    if (setVolume) setVolume(newVol);
    if (newVol > 0 && isMuted && setIsMuted) {
      setIsMuted(false);
      if (videoRef.current) videoRef.current.muted = false;
    }
  };

  const togglePausePlay = () => {
    if (!item?.CourseId) return;

    if (videoPlaying) {
      if (videoRef.current) videoRef.current.pause();
      if (onPauseVideo) onPauseVideo(item.CourseId);
    } else if (onPlayVideo) {
      onPlayVideo(item.CourseId);
    }
  };

  const toggleFullscreen = () => {
    if (!videoRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.log);
    } else {
      videoRef.current.requestFullscreen().catch((err) => {
        if (videoRef.current.webkitRequestFullscreen) videoRef.current.webkitRequestFullscreen();
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      // STRICT playback control from parent only.
      // hovered/hoveredId is used ONLY for showVideoLayer (mounting the controls/overlay),
      // not for deciding whether the video should actually be playing.
      const shouldPlay = playingId === item?.CourseId && isVideoPlaying;

      if (shouldPlay && !videoPlaying) {
        videoRef.current.play().catch((e) => console.log("Play prevented:", e));
      } else if (!shouldPlay && videoPlaying) {
        videoRef.current.pause();
      }

      videoRef.current.muted = isMuted;
      videoRef.current.volume = volume;
    }
  }, [isVideoPlaying, playingId, item?.CourseId, isMuted, volume, videoPlaying]);

  const handlePlayOverlay = () => {
    // IMPORTANT: call parent handler so Thumb state (icon + badge) stays in sync
    if (onPlayVideo && item?.CourseId) {
      onPlayVideo(item.CourseId);
    }
  };

  const showVideoLayer = playingId === item?.CourseId || 
                        (hovered && hoveredId === item?.CourseId);

  const showControls = videoPlaying && showVideoLayer;

  return (
    <>
      <div className="banner-carousel-card position-relative overflow-hidden">
        <img
          className="d-block w-100"
          src={item?.CourseImageURL200H}
          alt={item?.CourseTitle || "course banner"}
        />

        {showVideoLayer && (
          <div className="video-container position-absolute top-0 start-0 w-100 h-100">
            <video
              ref={videoRef}
              className="w-100 h-100 object-fit-cover"
              autoPlay={false}
              muted={isMuted}
              loop
              playsInline
              poster={item?.CourseImageURL200H || "./images/videobanner.png"}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
            >
              <source src={item?.CourseIntroVideoURL} type="video/mp4" />
              Your browser does not support the video tag.
            </video>

            {!videoPlaying && (
              <div
                className="play-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ background: "rgba(0,0,0,0.35)", zIndex: 5, cursor: "pointer" }}
                onClick={handlePlayOverlay}
              >
                <button
                  className="btn btn-light rounded-circle shadow-lg"
                  style={{ width: "68px", height: "68px", fontSize: "1.6rem", border: "none" }}
                  onClick={(e) => { e.stopPropagation(); handlePlayOverlay(); }}
                  aria-label="Play intro video"
                >
                  <FontAwesomeIcon icon={faPlay} />
                </button>
              </div>
            )}

            {showControls && (
              <div
                className="video-controls position-absolute d-flex flex-column align-items-end gap-2"
                style={{ top: '25%', right: '12px', zIndex: 30 }}
              >
                <button
                  className="btn btn-dark btn-sm rounded-circle shadow p-2"
                  onClick={togglePausePlay}
                  aria-label={videoPlaying ? "Pause video" : "Play video"}
                  style={{ width: '38px', height: '38px' }}
                >
                  <FontAwesomeIcon icon={videoPlaying ? faPause : faPlay} size="lg" />
                </button>

                <div
                  className="d-flex align-items-center"
                  onMouseEnter={() => setShowVolSlider(true)}
                  onMouseLeave={() => setShowVolSlider(false)}
                >
                  <button
                    className="btn btn-dark btn-sm rounded-circle shadow p-2"
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                    style={{ width: '38px', height: '38px' }}
                  >
                    <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} size="lg" />
                  </button>

                  {showVolSlider && (
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="form-range ms-2"
                      style={{ width: "90px", accentColor: "#0d6efd" }}
                      aria-label="Volume"
                    />
                  )}
                </div>

                <button
                  className="btn btn-dark btn-sm rounded-circle shadow p-2"
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  style={{ width: '38px', height: '38px' }}
                >
                  <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} size="lg" />
                </button>
              </div>
            )}
          </div>
        )}

        <div className={`carousel-caption ${videoPlaying && showVideoLayer ? "fade-out" : ""}`}>
          <div className="caption-head">
            <h3>{item?.CourseTitle}</h3>
            {item?.CourseDescription && (
              <p className="course-description text-white small mb-2" style={{ opacity: 0.9, fontSize: "0.9rem", lineHeight: "1.35" }}>
                {getCleanDescription(item.CourseDescription)}
              </p>
            )}
          </div>

          <div className="caption-tags">
            <div className="tag"><FontAwesomeIcon icon={faLaptopCode} /><span>48,000+ students taught</span></div>
            <div className="tag"><FontAwesomeIcon icon={faBook} /><span>60+ courses</span></div>
            <div className="tag"><FontAwesomeIcon icon={faThumbsUp} /><span>2,400+ reviews</span></div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BannerSliderCard;
